import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  ActionIcon,
  Alert,
  Box,
  Button,
  CopyButton,
  Flex,
  Group,
  Pagination,
  ScrollArea,
  Skeleton,
  Table,
  Tabs,
  Text,
  Tooltip
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconExternalLink,
  IconInfoCircle,
  IconPlus
} from '@tabler/icons-react';

import { useAppSelector } from '@/store/hooks';
import { useGetAppointmentQuery } from '@/store/slices/api/appointmentApiSlice';
import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';
import {
  useGetPatientQuery,
  useGetPatientSchedulesQuery
} from '@/store/slices/api/patientsApiSlice';
import { appointmentStatusLabel, appointmentTypeLabel } from '@/utils/appointmentTypeLabel';
import formatDate from '@/utils/formatDate';
import { appointmentsManagedInRs, clinicSchedulesAutomatically } from '@/utils/runPatientScheduler';
import withErrorBoundary from '@/utils/withErrorBoundary';
import InfoMessage from '@/components/InfoMessage';
import { routes } from '@/routes';
import EditMenuIcon from '@/assets/icons/EditMenuIcon';
import PrintIcon from '@/assets/icons/PrintIcon';

import { DeviceStatus } from '@/pages/physician/analytics/constants';
import { DeviceVendor, getVendorDisplayName } from '@/types/matchDevice';
import { Patient, PatientSchedule } from '@/types/patient';

import { appointmentFrequencyLabel } from '../../TasksPage/TaskDetailPage/appointmentHover';
import DeviceScheduleModal from '../../DeviceScheduleModal';
import { scheduleDeviceInfo } from '../../DeviceScheduleModal/scheduleDeviceInfo';
import pageStyles from '../PatientOverviewPage.module.scss';

import styles from './ScheduleList.module.scss';
import PrintScheduleIndex from './SchedulePrint';

// Storage key for the display of infobar about processing schedules
const PROCESSING_SCHEDULES_ALERT_KEY = 'schedule_processing_alert';
const ALERT_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const setProcessingAlertTimestamp = () => {
  sessionStorage.setItem(PROCESSING_SCHEDULES_ALERT_KEY, Date.now().toString());
};

const getProcessingAlertTimestamp = (): number | null => {
  const timestamp = sessionStorage.getItem(PROCESSING_SCHEDULES_ALERT_KEY);
  return timestamp ? parseInt(timestamp, 10) : null;
};

const clearProcessingAlert = () => {
  sessionStorage.removeItem(PROCESSING_SCHEDULES_ALERT_KEY);
};

const shouldShowProcessingAlert = (): boolean => {
  const timestamp = getProcessingAlertTimestamp();
  if (!timestamp) return false;

  const now = Date.now();
  const elapsed = now - timestamp;

  if (elapsed > ALERT_DURATION) {
    clearProcessingAlert();
    return false;
  }

  return true;
};

const APPOINTMENT_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A projected row repeats a real appointment as `uuid-1`, `uuid-2`, and so on.
// Created and modified live on the source appointment, so the suffix is removed
// before the lookup. Any other id is left as-is and is not fetched.
const sourceAppointmentId = (id: string): string => {
  const projected = id.match(
    /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-\d+$/i
  );
  return projected?.[1] ?? id;
};

const scheduleStamp = (value?: string | null): string => {
  if (!value?.trim()) return '—';
  return formatDate(value, 'MM/DD/YY h:mm A') || '—';
};

const ScheduleFacts = ({
  schedule,
  showId
}: {
  schedule: PatientSchedule;
  showId: boolean;
}) => {
  const appointmentId = sourceAppointmentId(schedule.id);
  const needsTimes = !schedule.createdAt && !schedule.updatedAt;
  const { data, isFetching } = useGetAppointmentQuery(appointmentId, {
    skip: !APPOINTMENT_UUID.test(appointmentId) || !needsTimes
  });
  const created = schedule.createdAt || data?.createdAt;
  const modified = schedule.updatedAt || data?.updatedAt;
  const waiting = isFetching && !created && !modified;

  return (
    <Box className={styles.detailGrid}>
      <Text className={styles.detailLabel}>Frequency</Text>
      <Text className={styles.detailValue}>
        {appointmentFrequencyLabel(schedule.frequency, schedule.frequencyNumber)}
      </Text>
      <Text className={styles.detailLabel}>Created</Text>
      <Text className={styles.detailValue}>{waiting ? 'Loading…' : scheduleStamp(created)}</Text>
      <Text className={styles.detailLabel}>Modified</Text>
      <Text className={styles.detailValue}>{waiting ? 'Loading…' : scheduleStamp(modified)}</Text>
      {showId ? (
        <>
          <Text className={styles.detailLabel}>Id</Text>
          <Group gap={4} wrap="nowrap" className={styles.uuidRow}>
            <Text className={styles.uuid}>{appointmentId}</Text>
            <CopyButton value={appointmentId}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy appointment id'} withArrow>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="xs"
                    aria-label="Copy appointment id"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      copy();
                    }}>
                    <IconCopy size={12} />
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
        </>
      ) : null}
    </Box>
  );
};

const isSchedulableDeviceStatus = (status: unknown): boolean => {
  const n = Number(status);
  return (
    n === DeviceStatus.Active || n === DeviceStatus['New Implant'] || n === DeviceStatus.Pending
  );
};

const ScheduleList = () => {
  const { uuid: patientId } = useParams() as { uuid: string };
  const isInternalUser = useAppSelector(
    (state) => state.auth.user?.role?.includes('internal') ?? false
  );
  const [activeTab, setActiveTab] = useState<string>('scheduled');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [showProcessingAlert, setShowProcessingAlert] = useState(() => shouldShowProcessingAlert());
  const [openScheduleIds, setOpenScheduleIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const getFilterParams = () => {
    switch (activeTab) {
      case 'completed':
        return { status: 'completed' as const };
      case 'scheduled':
        return { status: 'scheduled' as const };
      case 'no-show':
        return { status: 'no show' as const };
      default:
        return {};
    }
  };

  const { data, isLoading, isFetching, error, refetch } = useGetPatientSchedulesQuery(
    {
      patientId,
      limit: 10,
      page: currentPage,
      order: 'DESC',
      ...getFilterParams()
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  const { data: printData, isLoading: isPrintLoading } = useGetPatientSchedulesQuery(
    {
      patientId,
      limit: 10,
      page: 1,
      ...{ status: 'scheduled' as const }
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !isPrintModalOpen
    }
  );

  const { data: patient } = useGetPatientQuery(patientId);

  const isPdfGenerationEnabled = patient?.clinic?.parent?.enableSchedulePdfGeneration ?? false;
  const {
    data: clinicData,
    isLoading: clinicLoading,
    isError: clinicError
  } = useGetOrgByIdQuery(patient?.clinic?.id ?? '', {
    skip: !patient?.clinic?.id
  });
  const clinicPending = Boolean(patient?.clinic?.id) && clinicLoading && !clinicError;

  const isAppointmentManagedInRs = appointmentsManagedInRs(clinicData?.organization);
  const canSetSchedules = clinicSchedulesAutomatically(clinicData?.organization);
  const devices = patient?.matchDevices;

  const schedules: PatientSchedule[] = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  const availableDevices =
    devices
      ?.filter((device) => isSchedulableDeviceStatus(device.status))
      .map((device) => scheduleDeviceInfo(device))
      .filter((device): device is NonNullable<typeof device> => Boolean(device)) ?? [];

  // Handle processing alert timer
  useEffect(() => {
    if (!showProcessingAlert) return;

    const timestamp = getProcessingAlertTimestamp();
    if (!timestamp) {
      setShowProcessingAlert(false);
      return;
    }

    const remainingTime = ALERT_DURATION - (Date.now() - timestamp);

    if (remainingTime <= 0) {
      setShowProcessingAlert(false);
      clearProcessingAlert();
      return;
    }

    const timeoutId = setTimeout(() => {
      setShowProcessingAlert(false);
      clearProcessingAlert();
    }, remainingTime);

    return () => clearTimeout(timeoutId);
  }, [showProcessingAlert]);

  const handleTabChange = (value: string | null) => {
    setActiveTab(value || 'scheduled');
    setCurrentPage(1);
    setOpenScheduleIds([]);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setOpenScheduleIds([]);
  };

  const toggleSchedule = (id: string) => {
    setOpenScheduleIds((current) =>
      current.includes(id) ? current.filter((openId) => openId !== id) : [...current, id]
    );
  };

  const handleEditScheduleClick = () => {
    setIsModalOpen(true);
  };
  const handlePrintScheduleClick = () => {
    setIsPrintModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalSuccess = () => {
    // Set the processing alert timestamp and show the alert
    setProcessingAlertTimestamp();
    setShowProcessingAlert(true);
    refetch();
  };

  const handleAlertClose = () => {
    setShowProcessingAlert(false);
    clearProcessingAlert();
  };

  const getDeviceDisplayName = (device: { vendor: string; type: string }): string => {
    const vendorKey = device?.vendor?.trim().toLowerCase() ?? '';
    const vendor = (Object.values(DeviceVendor) as string[]).includes(vendorKey)
      ? getVendorDisplayName(vendorKey as DeviceVendor)
      : vendorKey
          .split('_')
          .filter(Boolean)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    const type = device?.type || '';

    return [vendor, type].filter(Boolean).join(' ');
  };

  if (error) {
    return (
      <Flex direction={'column'} gap={'md'}>
        <InfoMessage
          title={'Error Loading Schedules'}
          message={'Please try refreshing the page or contact support if the issue persists.'}
          c="red"
        />
      </Flex>
    );
  }

  const renderLoadingRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Table.Tr key={`loading-${index}`}>
        <Table.Td>
          <Skeleton height={16} width="60%" />
        </Table.Td>
        <Table.Td>
          <Skeleton height={16} width="80%" />
        </Table.Td>
        <Table.Td>
          <Skeleton height={16} width="70%" />
        </Table.Td>
        <Table.Td>
          <Flex direction="column" gap={4}>
            <Skeleton height={16} width="50%" />
            <Skeleton height={12} width="70%" />
          </Flex>
        </Table.Td>
      </Table.Tr>
    ));
  };

  const renderDataRows = () => {
    return schedules.map((schedule) => {
      const open = openScheduleIds.includes(schedule.id);
      return (
        <Fragment key={schedule.id}>
          <Table.Tr
            className={styles.scheduleRow}
            role="button"
            tabIndex={0}
            aria-expanded={open}
            onMouseDown={(event) => {
              // Focusing the row scrolls it inside the rail and pulls it out from under the cursor.
              if (event.button === 0) event.preventDefault();
            }}
            onClick={() => toggleSchedule(schedule.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleSchedule(schedule.id);
              }
            }}>
            <Table.Td>
              <Group gap={6} wrap="nowrap">
                {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                <Text size="sm" fw={500}>
                  {schedule.scheduledDate
                    ? formatDate(schedule.scheduledDate, 'MM/DD/YY')
                    : 'Unassigned'}
                </Text>
              </Group>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{appointmentTypeLabel(schedule.type)}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{getDeviceDisplayName(schedule.device)}</Text>
            </Table.Td>

            <Table.Td>
              <Flex align="flex-start" gap={0} direction={'column'}>
                <Text size="sm" px={0}>
                  {appointmentStatusLabel(schedule.status)}
                </Text>

                {schedule.isReceived && schedule.status?.toLowerCase() === 'pending' && (
                  <Text size="xs" c="dimmed" px={0}>
                    (Eligible Tx Received)
                  </Text>
                )}

                <Group gap="xs">
                  {schedule.transmissionReportId && (
                    <Text
                      size="xs"
                      px={0}
                      className={styles.viewReportLink}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (schedule.transmissionReportId) {
                          navigate(
                            routes.physician.transmissionReportDetail(
                              schedule.transmissionReportId?.toString()
                            )
                          );
                        }
                      }}>
                      View Report <IconExternalLink size={12} />
                    </Text>
                  )}
                </Group>
              </Flex>
            </Table.Td>
          </Table.Tr>
          {open ? (
            <Table.Tr key={`${schedule.id}-details`}>
              <Table.Td colSpan={4} className={styles.detailCell}>
                <ScheduleFacts schedule={schedule} showId={isInternalUser} />
              </Table.Td>
            </Table.Tr>
          ) : null}
        </Fragment>
      );
    });
  };

  const scheduleActions = isPdfGenerationEnabled ? (
    <Flex gap={20} align="center">
      <Tooltip label="Edit schedule">
        <Box onClick={handleEditScheduleClick} style={{ cursor: 'pointer' }}>
          <EditMenuIcon />
        </Box>
      </Tooltip>
      <Tooltip label="Schedule PDF">
        <Box onClick={handlePrintScheduleClick} style={{ cursor: 'pointer' }}>
          <PrintIcon />
        </Box>
      </Tooltip>
    </Flex>
  ) : (
    <Flex gap={8} align="center">
      <Button
        size="xs"
        onClick={handleEditScheduleClick}
        variant="subtle"
        leftSection={<IconPlus size={16} />}>
        Edit Schedule
      </Button>
    </Flex>
  );

  return (
    <Flex direction="column" flex={1} mih={0} h="100%">
      <Box px={24} className={pageStyles.headerSectionContent}>
        <Flex justify="space-between" align="center" gap={12}>
          <Text className={pageStyles.sectionLabel}>PATIENT SCHEDULE</Text>
          {scheduleActions}
        </Flex>
      </Box>
      <ScrollArea
        className={pageStyles.scrollArea}
        classNames={{ viewport: styles.viewport }}
        type="auto"
        scrollbarSize={5}
        offsetScrollbars>
        <Box px={24} py={16}>
          <Flex direction={'column'}>
            <Flex className={styles.filterHeaderSection}>
              <Tabs
                classNames={{
                  tab: styles.filterTabs
                }}
                value={activeTab}
                onChange={handleTabChange}
                variant="pills">
                <Tabs.List style={{ flexWrap: 'nowrap' }}>
                  <Tabs.Tab value="all">All</Tabs.Tab>
                  <Tabs.Tab value="scheduled">Scheduled</Tabs.Tab>
                  <Tabs.Tab value="completed">Completed</Tabs.Tab>
                  <Tabs.Tab value="no-show">No Show</Tabs.Tab>
                </Tabs.List>
              </Tabs>
            </Flex>

            {showProcessingAlert && (
              <Alert
                variant="light"
                color="blue"
                title="Processing Schedule Changes"
                mt="md"
                icon={<IconInfoCircle />}
                withCloseButton
                onClose={handleAlertClose}>
                Schedule changes are being processed and may take around five minutes to appear.
                Please refresh the page or come back later to see the generated schedules.
              </Alert>
            )}

            <Flex direction={'column'} my={8}>
              <Table highlightOnHover stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th className={styles.tableHeading}>Date</Table.Th>
                    <Table.Th className={styles.tableHeading}>Type</Table.Th>
                    <Table.Th className={styles.tableHeading}>Device</Table.Th>
                    <Table.Th className={styles.tableHeading}>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {isLoading || isFetching ? renderLoadingRows() : renderDataRows()}
                </Table.Tbody>
              </Table>

              {!isLoading && !isFetching && schedules.length === 0 && (
                <InfoMessage
                  title={'No Schedules'}
                  message={
                    activeTab === 'all'
                      ? 'No schedules available for this patient.'
                      : activeTab === 'scheduled'
                        ? 'No scheduled appointments found for this patient.'
                        : activeTab === 'completed'
                          ? 'No completed appointments found for this patient.'
                          : 'No no-show appointments found for this patient.'
                  }
                />
              )}
            </Flex>
          </Flex>
        </Box>
      </ScrollArea>
      {!isLoading && !isFetching && totalPages > 1 && (data?.total ?? schedules.length) >= 8 && (
        <Flex justify="center" py="md" style={{ flexShrink: 0 }}>
          <Pagination
            value={currentPage}
            onChange={handlePageChange}
            total={totalPages}
            size="sm"
          />
        </Flex>
      )}

      <PrintScheduleIndex
        patient={patient ?? ({} as Patient)}
        modalOpen={isPrintModalOpen}
        setModalOpen={setIsPrintModalOpen}
        printData={printData}
        isLoading={isPrintLoading}
      />

      <DeviceScheduleModal
        isOpen={isModalOpen && !clinicPending}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        patientSFId={patientId}
        shouldShowDeviceSelector={true}
        availableDevices={availableDevices}
        isAppointmentManagedInRs={isAppointmentManagedInRs}
        canSetSchedules={canSetSchedules}
      />
    </Flex>
  );
};

const ScheduleListWithBoundary = withErrorBoundary(ScheduleList, {
  fallback: (
    <InfoMessage
      title={'Failed To Load Schedules'}
      message={'Please refresh the page or contact support if the issue persists.'}
    />
  )
});

export default ScheduleListWithBoundary;
